<template>
  <ModalComponent
    :submitButtonText="
      $t('forwards.picker.criteria.cod_calculator_modal.apply')
    "
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{
      $t("forwards.picker.criteria.cod_calculator_modal.title")
    }}</template>

    <template #body>
      <p class="text-body-tertiary">
        {{ $t("forwards.picker.criteria.cod_calculator_modal.description") }}
      </p>

      <div class="mb-3">
        <label class="form-label" :for="deliveryDateId">
          {{
            $t("forwards.picker.criteria.cod_calculator_modal.delivery_date")
          }}
        </label>
        <input
          :id="deliveryDateId"
          v-model="deliveryDate"
          type="date"
          class="form-control"
        />
      </div>

      <div class="mb-3">
        <label class="form-label" :for="maxAgeId">
          {{ $t("forwards.picker.criteria.cod_calculator_modal.max_age") }}
        </label>
        <MwhInputComponent
          :id="maxAgeId"
          v-model="maxAgeYears"
          :unit="$t('forwards.picker.criteria.cod_calculator_modal.years')"
        />
      </div>

      <p
        v-if="codFrom"
        class="mb-0 fw-semibold"
        data-testid="cod-calculator-result"
      >
        {{
          $t("forwards.picker.criteria.cod_calculator_modal.result", {
            date: codFrom,
          })
        }}
      </p>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.picker.cancel") }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="codFrom === null"
        @click="apply"
      >
        {{ $t("forwards.picker.criteria.cod_calculator_modal.apply") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import {
  codFromForMaxAge,
  DEFAULT_MAX_COD_AGE_YEARS,
  defaultCodDeliveryDate,
} from "@/forwards2/picker/pickerForm";
import { computed, ref, useId, watch } from "vue";

/**
 * Works out the earliest COD a device may have to be at most a given age on a delivery date, and
 * hands it to the parent as the criteria's COD from. Opens on the defaults every time, so a range
 * changed since the last use is reflected in the delivery date.
 */
const props = defineProps<{
  /** The last quarter of the picker's production range, e.g. `2027-Q4`. */
  productionUntil: string;
}>();

const emit = defineEmits<{
  apply: [codFrom: string];
}>();

const model = defineModel<boolean>({ required: true });

const deliveryDateId = useId();
const maxAgeId = useId();

const forceToggle = ref<(value: boolean) => void>();
const deliveryDate = ref("");
const maxAgeYears = ref<number | null>(DEFAULT_MAX_COD_AGE_YEARS);

const codFrom = computed(() =>
  codFromForMaxAge(deliveryDate.value, maxAgeYears.value)
);

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) resetToDefaults();
});

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
function setForceToggle(toggle: (value: boolean) => void) {
  forceToggle.value = toggle;
  toggle(model.value);
  if (model.value) resetToDefaults();
}

function resetToDefaults() {
  deliveryDate.value = defaultCodDeliveryDate(props.productionUntil);
  maxAgeYears.value = DEFAULT_MAX_COD_AGE_YEARS;
}

function apply() {
  if (codFrom.value === null) return;
  emit("apply", codFrom.value);
  close();
}

function close() {
  model.value = false;
}
</script>
